import { IHash, ICommand } from 'core-ts-types';
import { ORM } from 'utility-ts-orm';

/**
 * @what define command function
 * @why facade between presentation and business layers
 */
export interface ICommandDatasource {
  delay: IHash<number>;
  delaySettings: IHash<IDebounceOptions>;
  interceptors: ICommandInterceptorHash;
}

export interface ICommandIntegrations {
  runORM: boolean;
}

export interface ICommandInterceptorHash extends _.Dictionary<IInterceptor[]> {
  finally: IInterceptor[];
  catch: IInterceptor[];
  then: IInterceptor[];
}

export interface IInterceptor {
  (commandName: string, rtn: any, ...args: any[]): void;
}

export interface IDebounceOptions {
  leading: boolean;
}

export class CommandConfigurationProvider implements ng.IServiceProvider {
  constructor(private CommandDatasource: ICommandDatasource) {}

  delay(name: string, delay: number, settings?: IDebounceOptions): CommandConfigurationProvider {
    this.CommandDatasource.delay[name] = delay;
    if (!_.isUndefined(settings)) {
      this.CommandDatasource.delaySettings[name] = settings!;
    }
    return this;
  }

  on(event: string, fn: IInterceptor): CommandConfigurationProvider {
    this.CommandDatasource.interceptors[this.convertOldEventNames(event.toLowerCase())].push(fn);
    return this;
  }

  private convertOldEventNames(event: string): string {
    if (event === 'success') {
      return 'then';
    } else if (event === 'error') {
      return 'catch';
    } else {
      return event;
    }
  }

  command(filterByCommandName: string) {
    return {
      on: (event: string, fn: IInterceptor) => {
        this.on(event, (commandName: string, rtn: any, ...args: any[]) => {
          if (commandName === filterByCommandName) {
            fn(commandName, rtn, ...args);
          }
        });
      }
    };
  }

  $get() {
    return this;
  }
}

export class DefineCommandProvider implements ng.IServiceProvider {
  constructor(
    private CommandDatasource: ICommandDatasource,
    private commandConfigurationProvider: CommandConfigurationProvider
  ) {}

  configure(c: ICommandDatasource) {
    c = c || ({} as unknown as ICommandDatasource);

    _.each(c.delay, (delayedTime: number, delayedKey: string) => {
      this.commandConfigurationProvider.delay(delayedKey, delayedTime);
    });

    return this;
  }

  delay(name: string, delay: number) {
    this.commandConfigurationProvider.delay(name, delay);
    return this;
  }

  on(event: string, fn: IInterceptor) {
    this.commandConfigurationProvider.on(event, fn);
    return this;
  }

  $get($q: ng.IQService) {
    const self = this;

    const pDebounce = (fn: Function, wait: number, opts: IDebounceOptions): (() => ng.IPromise<any>) => {
      if (!Number.isFinite(wait)) {
        throw new TypeError('Expected `wait` to be a finite number');
      }

      opts = opts || ({} as unknown as IDebounceOptions);

      let leadingVal: number;
      let timer: number | undefined;
      let resolveList: any[] = [];

      return function (): ng.IPromise<any> {
        const args = arguments;

        return $q((resolve: ng.IQResolveReject<any>) => {
          const runImmediately = opts.leading && !timer;

          clearTimeout(timer as number);

          timer = window.setTimeout(() => {
            timer = undefined;

            const res = opts.leading ? leadingVal : fn.apply(self, args);

            for (resolve of resolveList) {
              resolve(res);
            }

            resolveList = [];
          }, wait);

          if (runImmediately) {
            leadingVal = fn.apply(self, args);
            resolve(leadingVal as any);
          } else {
            resolveList.push(resolve);
          }
        });
      };
    };

    const registerResponseHandler = (
      name: string,
      originalArguments: any[],
      deferred: ng.IDeferred<any>,
      eventName: string
    ) => {
      return function (rtn?: any) {
        _.each(self.CommandDatasource.interceptors[eventName], (fn: IInterceptor) => {
          fn(name, rtn, originalArguments);
        });

        return deferred.promise;
      };
    };

    return function defineCommand(
      name: string,
      commandFn: Function,
      commandFeatures: ICommandIntegrations = {
        runORM: false
      }
    ): ICommand {
      const commandExecutor = function commandExecutor(...args: any[]) {
        const deferred = $q.defer();
        const hasInterceptors =
          _.reduce(
            self.CommandDatasource.interceptors,
            function (total: number, interceptor: IInterceptor[]) {
              return total + interceptor.length;
            },
            0
          ) > 0;

        const value: ng.IDeferred<any> = commandFn(deferred, ...args);

        if (value !== undefined && !_.isFunction(value.resolve)) {
          deferred.resolve(value);
        }

        const returnPromiseChain: ng.IPromise<any> = commandFeatures.runORM
          ? deferred.promise.then((res) => {
              ORM.run();
              return res;
            })
          : deferred.promise;

        if (hasInterceptors) {
          return returnPromiseChain
            .then(registerResponseHandler(name, args, deferred, 'then'))
            .catch(registerResponseHandler(name, args, deferred, 'catch'))
            .finally(registerResponseHandler(name, args, deferred, 'finally'));
        }

        return returnPromiseChain;
      };

      return self.CommandDatasource.delay[name] === undefined
        ? commandExecutor
        : pDebounce(
            commandExecutor,
            self.CommandDatasource.delay[name],
            self.CommandDatasource.delaySettings[name] || {
              leading: false
            }
          );
    };
  }
}

angular
  .module('utility-ng-kernel')
  .constant('CommandDatasource', {
    delay: {},
    delaySettings: {},
    interceptors: {
      finally: [],
      catch: [],
      then: []
    }
  })
  .provider('commandConfiguration', CommandConfigurationProvider)
  .provider('defineCommand', DefineCommandProvider)
  .factory('defineCommandORM', function (defineCommand: Function) {
    return (...args: any[]) =>
      defineCommand(...args, {
        runORM: true
      });
  });



// WEBPACK FOOTER //
// ./app/vendor/utility-ng-kernel/src/core/command_builder.ts